// Detect se siamo su mobile
export function isMobileDevice(): boolean {
  if (typeof window === "undefined") return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  
  return mobileRegex.test(userAgent.toLowerCase()) || 
         ('ontouchstart' in window && window.innerWidth < 768);
}

// Check se un wallet è disponibile nel browser
export function hasWalletAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  return !!(
    (window as any).solana ||
    (window as any).phantom?.solana ||
    (window as any).solflare ||
    (window as any).backpack
  );
}

// Detect quale wallet mobile è installato
export function detectMobileWallet(): string | null {
  if (typeof window === "undefined") return null;
  
  if ((window as any).phantom?.solana?.isPhantom) return "phantom";
  if ((window as any).solflare?.isSolflare) return "solflare";
  if ((window as any).backpack?.isBackpack) return "backpack";
  if ((window as any).solana?.isPhantom) return "phantom";
  
  return null;
}

// Deep link per aprire wallet app
export function getWalletDeepLink(walletName: string, returnUrl?: string): string {
  const targetUrl = returnUrl || window.location.href;
  const encodedUrl = encodeURIComponent(targetUrl);
  const encodedRef = encodeURIComponent(window.location.origin);

  switch (walletName.toLowerCase()) {
    case "phantom":
      // Phantom usa il formato browse con URL completo encodato
      return `https://phantom.app/ul/browse/${encodedUrl}`;
    case "solflare":
      // Solflare: usa schema deeplink nativo (più affidabile su mobile) + ref obbligatorio
      // Docs: https://docs.solflare.com/solflare/technical/deeplinks/other-methods/browse
      return `solflare://ul/v1/browse/${encodedUrl}?ref=${encodedRef}`;
    case "backpack":
      return `https://backpack.app/ul/browse/${encodedUrl}`;
    default:
      return `https://phantom.app/ul/browse/${encodedUrl}`;
  }
}

// Salva stato prima di redirect a wallet app
export function saveStateBeforeRedirect(state: Record<string, any>): void {
  try {
    sessionStorage.setItem("walletRedirectState", JSON.stringify({
      ...state,
      timestamp: Date.now(),
      returnUrl: window.location.href
    }));
  } catch (e) {
    console.error("Failed to save redirect state:", e);
  }
}

// Recupera stato dopo ritorno da wallet app
export function restoreStateAfterRedirect(): Record<string, any> | null {
  try {
    const saved = sessionStorage.getItem("walletRedirectState");
    if (!saved) return null;
    
    const state = JSON.parse(saved);
    // Scade dopo 5 minuti
    if (Date.now() - state.timestamp > 5 * 60 * 1000) {
      sessionStorage.removeItem("walletRedirectState");
      return null;
    }
    
    return state;
  } catch (e) {
    console.error("Failed to restore redirect state:", e);
    return null;
  }
}

// Pulisce stato redirect
export function clearRedirectState(): void {
  try {
    sessionStorage.removeItem("walletRedirectState");
  } catch (e) {
    console.error("Failed to clear redirect state:", e);
  }
}

// Pulisce cache wallet mobile
export function clearMobileWalletCache(): void {
  try {
    localStorage.removeItem("detectedMobileWallet");
    localStorage.removeItem("walletName");
    sessionStorage.removeItem("walletRedirectState");
  } catch (e) {
    console.error("Failed to clear mobile wallet cache:", e);
  }
}

// Applica fix per wallet mobile (chiamare in main.tsx)
export function applyMobileWalletFix(): void {
  if (!isMobileDevice()) return;
  
  // Fix per persistenza wallet dopo redirect
  const originalGetItem = localStorage.getItem.bind(localStorage);
  localStorage.getItem = (key: string) => {
    const value = originalGetItem(key);
    
    // Se wallet disconnesso ma abbiamo salvato uno, ripristinalo
    if (key === "walletName" && !value) {
      const detected = detectMobileWallet();
      if (detected) {
        return detected;
      }
    }
    
    return value;
  };
  
  // Fix per Safari BFCache
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      const detected = detectMobileWallet();
      if (detected) {
        localStorage.setItem("detectedMobileWallet", detected);
      }
    }
  });
  
  // Ripristina la pagina dopo ritorno dal wallet
  const restorePageAfterWalletRedirect = () => {
    const state = restoreStateAfterRedirect();
    if (state && state.page && state.page !== window.location.pathname) {
      console.log("📍 Restoring page after wallet redirect:", state.page);
      // Non fare nulla - lascia che l'utente resti dove è
      // Il wallet in-app browser mantiene già la pagina corretta
    }
    // Pulisci lo stato dopo il controllo
    clearRedirectState();
  };
  
  // Esegui al caricamento
  if (document.readyState === "complete") {
    restorePageAfterWalletRedirect();
  } else {
    window.addEventListener("load", restorePageAfterWalletRedirect);
  }
}
